import { useEffect, useState } from 'react';
import { useJobHistory, type JobRecord } from '../services/jobMonitor';

/**
 * Admin Job Monitor — a hidden debug panel listing every job started this page
 * load and its live status. Dev/admin tool, NOT the customer "Recent Portraits".
 *
 * Visible only with ?apb_debug=1 in the URL. Ctrl+Shift+D toggles it (also
 * gated on ?apb_debug=1). Data comes from the in-memory jobMonitor store fed by
 * useGenerateFlow; nothing is persisted — it resets on reload.
 */

function isDebug(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('apb_debug') === '1';
  } catch {
    return false;
  }
}

const BADGE: Record<JobRecord['status'], { bg: string; label: string }> = {
  pending:    { bg: '#666666', label: 'PENDING' },
  processing: { bg: '#f59e0b', label: 'PROCESSING' },
  completed:  { bg: '#22c55e', label: 'COMPLETED' },
  failed:     { bg: '#ef4444', label: 'FAILED' },
};

function fmtClock(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('en-GB', { hour12: false });
}

function durationSecs(rec: JobRecord, now: number): string {
  const start = new Date(rec.started_at).getTime();
  if (Number.isNaN(start)) return '—';
  const end = rec.completed_at ? new Date(rec.completed_at).getTime() : now;
  return `${Math.max(0, Math.round((end - start) / 1000))}s`;
}

export function JobMonitor() {
  const debug = isDebug();
  const jobs = useJobHistory();
  const [open, setOpen] = useState(true);
  const [now, setNow] = useState(() => Date.now());

  // Ctrl+Shift+D toggles the panel — only when ?apb_debug=1.
  useEffect(() => {
    if (!debug) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [debug]);

  // Tick every 2s so live durations advance while a job is still processing.
  const hasProcessing = jobs.some((j) => j.status === 'processing');
  useEffect(() => {
    if (!debug || !open || !hasProcessing) return;
    const id = window.setInterval(() => setNow(Date.now()), 2000);
    return () => window.clearInterval(id);
  }, [debug, open, hasProcessing]);

  if (!debug || !open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        bottom: 0,
        width: 420,
        maxWidth: '100vw',
        maxHeight: '60vh',
        overflowY: 'auto',
        background: '#1a1a1a',
        color: '#fff',
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.5,
        zIndex: 9999,
        borderRadius: '8px 8px 0 0',
        padding: 12,
        boxShadow: '0 -4px 24px rgba(0,0,0,0.5)',
      }}
    >
      <style>{`@keyframes bf-blink{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>🔧 APB Job Monitor</strong>
        <span style={{ marginLeft: 'auto', opacity: 0.7 }}>
          {jobs.length} job{jobs.length === 1 ? '' : 's'} this session
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          style={{
            background: 'transparent',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            padding: '0 4px',
          }}
        >
          ×
        </button>
      </div>

      {jobs.length === 0 ? (
        <div style={{ opacity: 0.6, padding: '8px 0' }}>No jobs yet this session.</div>
      ) : (
        jobs.map((j) => {
          const badge = BADGE[j.status];
          return (
            <div
              key={j.job_id}
              style={{
                borderTop: '1px solid #333',
                padding: '8px 0',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{
                    background: badge.bg,
                    color: '#000',
                    fontWeight: 700,
                    fontSize: 10,
                    padding: '1px 6px',
                    borderRadius: 4,
                    whiteSpace: 'nowrap',
                    animation: j.status === 'processing' ? 'bf-blink 1s ease-in-out infinite' : undefined,
                  }}
                >
                  {badge.label}
                </span>
                <span style={{ opacity: 0.9, wordBreak: 'break-all' }}>
                  {j.job_id.length > 20 ? `${j.job_id.slice(0, 20)}…` : j.job_id}
                </span>
              </div>

              <div style={{ opacity: 0.8, marginTop: 2 }}>
                {(j.portrait_mode || '(none)')} | {j.style_name || j.style_id || '(style?)'}
              </div>

              <div style={{ opacity: 0.6, marginTop: 2 }}>
                Started: {fmtClock(j.started_at)} &nbsp;|&nbsp; Duration: {durationSecs(j, now)}
              </div>

              {j.status === 'completed' && j.preview_url && (
                <div style={{ marginTop: 2 }}>
                  <a
                    href={j.preview_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#60a5fa' }}
                  >
                    open preview ↗
                  </a>
                </div>
              )}

              {j.status === 'failed' && j.error && (
                <div style={{ color: '#fca5a5', marginTop: 2, wordBreak: 'break-word' }}>
                  {j.error}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
